import { defaultConfig } from './catalogue';
import type { FlowGraph } from './graph';
import { indexGraph, topologicalOrder } from './resolve';
import type { PortCounts } from './types';

/** What one rendered output costs. */
export const CREDITS_PER_RENDER = 4;

export interface Estimate {
  /** Items in (per input port) and out (per output port) for each node. */
  nodes: Record<string, { in: PortCounts; out: PortCounts }>;
  /** Items along each edge. */
  edges: Record<string, number>;
  renders: number;
  credits: number;
  /** Some count depends on data (a filter, a review), so these are upper bounds: show "~". */
  approximate: boolean;
}

/**
 * Walks the graph in order, multiplying counts by each node's cardinality.
 * `observed` pins a node's output count (a starter preview, or what a filter
 * let through last run) in place of its upper bound.
 */
export function estimate(
  graph: FlowGraph,
  observed: Record<string, number> = {},
): Estimate {
  const index = indexGraph(graph);
  const result: Estimate = {
    nodes: {},
    edges: {},
    renders: 0,
    credits: 0,
    approximate: false,
  };

  for (const id of topologicalOrder(index).order) {
    const entry = index.nodes.get(id);
    if (!entry?.definition) continue;
    const { node, definition } = entry;

    const inputs: PortCounts = {};
    for (const port of definition.inputs) {
      if (port.accepts !== 'trigger') inputs[port.id] = 0;
    }
    for (const edge of index.incoming.get(id) ?? []) {
      if (edge.target.port in inputs) {
        inputs[edge.target.port] =
          (inputs[edge.target.port] ?? 0) + (result.edges[edge.id] ?? 0);
      }
    }

    const parsed = definition.config.safeParse(node.config);
    const config: unknown = parsed.success
      ? parsed.data
      : { ...defaultConfig(definition), ...node.config };
    let outputs: PortCounts;
    try {
      outputs = definition.cardinality(config, inputs);
    } catch {
      outputs = {}; // a config too broken to count; validation reports it
    }
    const pinned = observed[id];
    if (pinned !== undefined) {
      outputs = Object.fromEntries(Object.keys(outputs).map((port) => [port, pinned]));
    } else if (definition.bounded) {
      result.approximate = true;
    }

    const emitted = Object.values(outputs).reduce((sum, count) => sum + count, 0);
    result.renders += emitted * (definition.rendersPerItem ?? 0);
    result.nodes[id] = { in: inputs, out: outputs };
    for (const edge of index.outgoing.get(id) ?? []) {
      result.edges[edge.id] = outputs[edge.source.port] ?? 0;
    }
  }

  result.credits = result.renders * CREDITS_PER_RENDER;
  return result;
}
